// Pay attention! Methods of this service are moved and refactored from
// 1) Ordering/modules/OrderEntry/service
// 2) Ordering/modules/Exit/ExitService
// 3) Ordering/modules/ModifyOrder/service
// 4) OrderEntryService extends TBOrderEntryStore

import OrdersTVService from 'modules/TradesPW3/Orders';
import AnalyticsService from 'modules/Analytics';
import PriceAlerts from 'modules/PriceAlerts';
import LiveData from 'modules/LiveData';
import Format from 'modules/Format';
import { decomposeInstrumentKey, decomposeSegment } from 'modules/BOD/modules/Search/utils';

import {
  ORDER_TYPE,
  SIDES,
  Products,
  OrderRequestMode,
  OrderCategory,
  CommonOrderSide,
  CommonOrderValidity,
  COMMON_ORDER_TYPE,
} from 'modules/Ordering/consts';
import { CleverTapService } from 'modules/CleverTap';
import { EVENTS } from 'modules/Analytics/consts';

class OrderingServiceFormatter {
  private generatePriceDataForOrder(
    order: OrderWithCustomFields,
  ): Partial<CommonOrder> {
    const { type, limitPrice, stopPrice } = order;
    let orderData = {};
    if (ORDER_TYPE[type] === 'L') {
      orderData = {
        ...orderData,
        price: limitPrice,
      };
    }

    if (ORDER_TYPE[type] === 'SL-M') {
      orderData = {
        ...orderData,
        stopLossTrigger: stopPrice,
      };
    }

    if (ORDER_TYPE[type] === 'SL') {
      orderData = {
        ...orderData,
        price: limitPrice,
        stopLossTrigger: stopPrice,
      };
    }
    return orderData;
  }

  generateCommonOrderRequestData(
    params: OrderWithCustomFields,
    surveillanceShown?: boolean,
  ): CommonOrdersRequest {
    const {
      customFields: { productType = 'I' } = {},
      qty,
      side,
      type,
      duration: { type: validity },
      instrumentKey,
    } = params;

    const liveData = LiveData.getByInstrumentKey(
      instrumentKey || '',
    );

    let commonOrders: CommonOrder = {
      orderCategory: OrderCategory.REGULAR,
      additionalInfo: {
        surveillance: !!surveillanceShown,
        itemId: Format.generateUUID(),
        orderLtp: Number(liveData?.lastTradedPrice),
      },
      amo: false,
      disclosedQuantity: 0,
      instrumentKey,
      orderType: COMMON_ORDER_TYPE[type],
      price: 0,
      validity,
      quantity: Number(qty),
      productType,
      side: CommonOrderSide[SIDES[side]],
    };

    const priceOrderData = this.generatePriceDataForOrder(params);
    commonOrders = {
      ...commonOrders,
      ...priceOrderData,
    };

    const commonOrderData: CommonOrdersRequest = {
      context: 'TV',
      requestMode: OrderRequestMode.REGULAR,
      orderTime: Date.now(),
      orders: [commonOrders],
    };

    return commonOrderData;
  }

  generateModifyPlaceOrderData = (tvOrder: OrderWithCustomFields) => {
    const {
      id,
      type,
      qty,
      duration: { type: validity },
    } = tvOrder;
    const order = OrdersTVService.findOrderById(id || '');

    if (order) {
      const {
        product,
        side,
        orderNumber: { oms, orderRefId },
      } = order;

      let commonOrders: CommonOrder = {
        omsId: oms,
        upstoxId: orderRefId,
        orderCategory: OrderCategory.REGULAR,
        amo: false,
        disclosedQuantity: 0,
        instrumentKey: order.instrument_key,
        orderType: COMMON_ORDER_TYPE[type],
        price: 0,
        validity,
        quantity: Number(qty),
        productType: product,
        side: CommonOrderSide[side],
      };

      const priceOrderData = this.generatePriceDataForOrder(tvOrder);
      commonOrders = {
        ...commonOrders,
        ...priceOrderData,
      };

      const commonOrderData: CommonOrdersRequest = {
        context: 'TV',
        requestMode: OrderRequestMode.REGULAR,
        orderTime: Date.now(),
        orders: [commonOrders],
      };

      return commonOrderData;
    }
  };

  getOrderTypeFromOrder(product: Product) {
    switch (product) {
      case 'I':
        return 'Intraday';
      case 'D':
        return 'Delivery';
      default:
        return null;
    }
  }

  getComplexityFromOrder(product: Product) {
    switch (product) {
      case 'CO':
        return 'CO';
      case 'OCO':
        return 'OCO';
      default:
        return null;
    }
  }

  getOrderType(
    formData: Pick<
      OrderEntryFormData,
      'limit_price_checkbox' | 'trigger_price_checkbox'
    >,
  ): PlaceOrderRequest['orderType'] {
    const { limit_price_checkbox, trigger_price_checkbox } = formData;

    if (limit_price_checkbox && trigger_price_checkbox) return 'SL';
    if (limit_price_checkbox && !trigger_price_checkbox) return 'L';
    if (!limit_price_checkbox && trigger_price_checkbox) return 'SL-M';

    return 'M';
  }

  generateDataForCheckEdis = (
    data: OrderWithCustomFields,
  ): {
    productType: OrderEntryType;
    side: OrderEntrySide;
  } => {
    const { side, customFields: { productType = 'I' } = {} } = data;

    return {
      productType: Products[productType] as OrderEntryType,
      side: side === 1 ? 'Buy' : 'Sell',
    };
  };

  sendMixpanelEventForCommonOrderPlaced = (
    orderStatus: MixpanelOrderStatuses,
    data: CommonOrder,
    scripData: DataSchema | null,
    instantOrderEnabled: boolean,
  ) => {
    const { segment } = decomposeInstrumentKey(data.instrumentKey);
    const { exchange } = decomposeSegment(segment);

    const alert = PriceAlerts
      ?.priceAlertList
      ?.find(item => item.instrumentKey === data.instrumentKey);
    // Triggered Alert
    const triggered = alert
      ?.rules
      ?.find(item => item.status === 'TRIGGERED') ? 'Yes' : 'No';

    const eventData = {
      Exchange: exchange,
      Side: data.side === 'BUY' ? 'Buy' : 'Sell',
      Quantity: data.quantity,
      Symbol: scripData?.symbol,
      'Order Type': data.orderType,
      Price: data.price || data.stopLossTrigger,
      'Disclosed Quantity': data.disclosedQuantity,
      Validity: data.validity || CommonOrderValidity.D,
      'Disclosed Qty Toggle': !!data.disclosedQuantity,
      'Order Status': orderStatus,
      'Product Type': Products[data.productType],
      'Trigger Price': data?.stopLossTrigger,
      'Trigger Price toggle': !!data?.stopLossTrigger,
      'Limit Price toggle': !!data?.price,
      'Limit Price': data?.price,
      Segment: segment,
      Scrip: scripData?.name,
      'Price Alert Trigger': triggered,
      isInstantOrder: instantOrderEnabled ? 'Yes' : 'No',
    };

    switch (orderStatus) {
      case 'Placed':
        AnalyticsService.sendEvent(EVENTS.ORDER_PLACED, eventData);
        CleverTapService.sendEvent(`Order ${orderStatus}`, {
          Scrip: eventData.Scrip,
          Segment: eventData.Segment,
          Side: eventData.Side,
        });
        break;
      case 'Modified':
        AnalyticsService.sendEvent(EVENTS.ORDER_MODIFIED, eventData);
        break;
      case 'Executed':
        CleverTapService.sendEvent(`Order ${orderStatus}`, {
          Scrip: eventData.Scrip,
          Segment: eventData.Segment,
          Side: eventData.Side,
        });
        break;
      default:
        break;
    }
  }
}

export default new OrderingServiceFormatter();
